import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser } from 'aws-amplify/auth';

/**
 * OAuthCallback — handles the redirect from Cognito after Google OAuth completes.
 *
 * Amplify v6 automatically exchanges the authorization code for tokens when the
 * app loads on the callback URL. We listen for the 'signedIn' Hub event and then
 * redirect to the dashboard, or show an error if the flow failed.
 */
export default function OAuthCallback() {
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // Amplify processes the code exchange on page load. Check if we're already signed in
    // (race-condition: Hub event may fire before this effect, or we arrived here signed in).
    getCurrentUser()
      .then(() => navigate('/', { replace: true }))
      .catch(() => {
        // Not signed in yet — wait for Amplify to finish the code exchange
        const unlisten = Hub.listen('auth', ({ payload }) => {
          if (payload.event === 'signedIn') {
            unlisten();
            navigate('/', { replace: true });
          }
          if (payload.event === 'signInWithRedirect_failure') {
            unlisten();
            navigate('/login?error=oauth_failed', { replace: true });
          }
        });

        // Safety timeout: if nothing happens in 10s, send back to login
        const timeout = setTimeout(() => {
          unlisten();
          navigate('/login?error=oauth_timeout', { replace: true });
        }, 10_000);

        return () => {
          unlisten();
          clearTimeout(timeout);
        };
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm text-gray-600">Finishing sign in…</p>
      </div>
    </div>
  );
}
